import React, { useMemo, useEffect } from 'react';
import { Box, Heading, Text, Icon } from "@chakra-ui/react";
import { CardCarousel } from "./MiniCard/CardCarousel";
import FeaturedCarousel from "./MiniCard/FeaturedCarousel";
import { useCategoriesData, getUniqueTags, filterByTag } from "./Data/Data";
import { useSelector, useDispatch } from "react-redux";
import { FaFilm } from "react-icons/fa";
import FileSystemService from '../../services/FileSystemService';
import { setCurrentManifest } from '../../Redux/Mode/action';

function Home() {
  // Use the hook to get mode-aware data
  const categories = useCategoriesData();
  const dispatch = useDispatch();
  const movies = useSelector(state => state.Mode?.dataSource?.movies || []);
  
  // Load home-specific manifest on component mount
  useEffect(() => {
    // Load home-specific manifest on component mount
      FileSystemService.getManifests().then(manifestsResult => {
        if (manifestsResult.success && manifestsResult.manifests?.length > 0) {
          // Find standard manifest files (not the specialized ones)
          const homeManifests = manifestsResult.manifests.filter(name => 
            name.startsWith('burrow-stream-manifest-') && 
            !name.includes('-films-') && 
            !name.includes('-shorts-') && 
            !name.includes('-clips-'));
            
          if (homeManifests.length > 0) {
            // Sort by date and use most recent
            const sortedManifests = [...homeManifests].sort().reverse();
            const latestManifest = sortedManifests[0];
            console.log(`Loading home manifest: ${latestManifest}`);
            dispatch(setCurrentManifest(latestManifest));
          }
        }
      });
  }, [dispatch]);

  return (
    <Box 
      mt={{ base: "72px", md: "88px" }}
      backgroundImage={`url('${process.env.PUBLIC_URL}/media/BurrowBackLight1.png')`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="repeat"
      backgroundAttachment="fixed"
    >
      
      {/* Dynamically generated tag-based rows for Home page */}
      <Box pt={{ base: 2, md: 4, lg: 6 }}>
        {/* Burrow Picks - large featured cards, stands out from the rest */}
        <FeaturedCarousel
          data={categories.burrowPicks || []}
          title="Burrow Picks"
        />

        {/* First, show a few selected important categories */}
        <CardCarousel
          data={categories.test}
          title="Most Popular"
          minititle="Add to your Up Next watchlist today."
          iconName="FaMedal"
          titleColor="#1f4037"
        />
        
        <CardCarousel 
          data={categories.burrowUpdates} 
          title="Burrow Updates"
          iconName="FaNewspaper"
          titleColor="#a2ab58"
        />
        
        {/* Then dynamically generate rows for all unique tags in the movie collection, in priority order */}
        {(() => {
          // Define tag priority order
          const tagPriorityOrder = [
            "disclosure hub films",
            "secret civics",
            "boom tapes",
            "daring doctors",
            "injection justice",
            "higher realities",
            "microwave technology",
            "medical mythology and nano tech",
            "health wealth",
            "god given rights v law",
            "big tech take over",
            "public displays",
            "relationship wars",
            "cloning and genetic engineering",
            "cryptids and ufos",
            "general conspiracy",
            "free energy potential",
            "false idols and trauma",
            "dark elite occult",
            "exotic journalism",
            "agenda 2030 wef nwo and fema",
            "the 911 archive",
            "q and team non bias archive",
            "weather modification",
            "zombies database"
          ];
          
          // Get all unique tags
          const uniqueTags = getUniqueTags(movies);
          
          // Sort tags according to priority order (tags in priority list come first, in specified order)
          const sortedTags = [...uniqueTags].sort((a, b) => {
            // Skip tags that already have dedicated sections
            if (a === "burrow picks" || a === "burrow updates" || a === "test") {
              return 1; // Push to the end
            }
            if (b === "burrow picks" || b === "burrow updates" || b === "test") {
              return -1; // Push to the end
            }
            
            // Get priority indices (default to high number if not in priority list)
            const aIndex = tagPriorityOrder.indexOf(a.toLowerCase());
            const bIndex = tagPriorityOrder.indexOf(b.toLowerCase());
            
            // Both are in priority list, sort by priority
            if (aIndex !== -1 && bIndex !== -1) {
              return aIndex - bIndex;
            }
            
            // Only a is in priority list
            if (aIndex !== -1) {
              return -1; // a comes first
            }
            
            // Only b is in priority list
            if (bIndex !== -1) {
              return 1; // b comes first
            }
            
            // Neither is in priority list, sort alphabetically
            return a.localeCompare(b);
          });
          
          // Map the sorted tags to carousel components
          return sortedTags.map((tag, index) => {
            // Skip "burrow picks" as it's already featured at the top
            if (tag === "burrow picks" || tag === "burrow updates" || tag === "test") {
              return null;
            }
            
            // Get movies with this tag
            const taggedMovies = filterByTag(movies, tag);
            
            // Only render row if it has enough content (at least 2 videos)
            if (taggedMovies.length < 2) {
              return null;
            }
            
            // Get the category style for icon
            const lowercaseTag = tag.toLowerCase();
            
            // Import the functions and icon map from CategoryCard.jsx
            const getCategoryStyle = (tagName) => {
              // This is a simplified version of the function from CategoryCard.jsx
              const categoryMap = {
                "burrow picks": { icon: "FaTrophy", color: "#FFD700" },
                "boom tapes": { icon: "FaBolt", color: "#4B79A1" },
                "cryptids and ufos": { icon: "GiAlienStare", color: "#9370DB" },
                "daring doctors": { icon: "FaUserMd", color: "#E3342F" },
                "microwave technology": { icon: "FaBroadcastTower", color: "#4A5568" },
                "microwave tech": { icon: "FaBroadcastTower", color: "#4A5568" },
                "big tech take over": { icon: "GiRobotGolem", color: "#1A202C" },
                "disclosure hub films": { icon: "FaFilm", color: "#D69E2E" }, // Switched with public displays
                "secret civics": { icon: "FaUniversity", color: "#2F855A" },
                "occult katt clips": { icon: "FaEye", color: "#B83280" },
                "injection justice": { icon: "FaSyringe", color: "#C53030" },
                "higher realities": { icon: "GiCrystalBall", color: "#4A5568" },
                "health wealth": { icon: "GiTreeBranch", color: "#48BB78" },
                "god given rights law": { icon: "FaBalanceScale", color: "#4299E1" },
                "god given rights v law": { icon: "FaBalanceScale", color: "#4299E1" },
                "public displays": { icon: "FaBinoculars", color: "#3182CE" }, // Switched with disclosure hub films
                "relationship wars": { icon: "FaHeartBroken", color: "#E53E3E" },
                "genetic engineering": { icon: "FaDna", color: "#38B2AC" },
                "cloning and genetic engineering": { icon: "FaDna", color: "#38B2AC" },
                "general conspiracy": { icon: "GiMoai", color: "#2B6CB0" },
                "free energy": { icon: "FaMagnet", color: "#805AD5" },
                "free energy potential": { icon: "FaMagnet", color: "#805AD5" },
                "dark elite occult": { icon: "GiOni", color: "#1A202C" },
                "wef nwo and fema": { icon: "FaDraftingCompass", color: "#3B82F6" },
                "agenda 2030 wef nwo and fema": { icon: "FaDraftingCompass", color: "#3B82F6" },
                "q team archive": { icon: "FaHourglassHalf", color: "#DD6B20" },
                "q and team non bias archive": { icon: "FaHourglassHalf", color: "#DD6B20" },
                "the 911 archive": { icon: "FaPlane", color: "#C53030" },
                "false idol trauma": { icon: "PiSpiralBold", color: "#667EEA" },
                "false idols and trauma": { icon: "PiSpiralBold", color: "#667EEA" },
                "weather modification": { icon: "WiTornado", color: "#63B3ED" },
                "weather modifcation": { icon: "WiTornado", color: "#63B3ED" },
                "zombies database": { icon: "FaGhost", color: "#4b6cb7" },
                "exotic journalism": { icon: "FaNewspaper", color: "#a2ab58" },
                "medical mythology and nano tech": { icon: "FaFlask", color: "#2193b0" },
                "medical mythology": { icon: "FaFlask", color: "#2193b0" },
                "test": { icon: "FaMedal", color: "#1f4037" },
                "most popular": { icon: "FaMedal", color: "#1f4037" },
                "burrow updates": { icon: "FaNewspaper", color: "#a2ab58" }
              };
              
              if (categoryMap[tagName]) {
                return categoryMap[tagName];
              }
              
              // Return default if no match found
              return { icon: "BsStars", color: "#302b63" };
            };
            
            // Format the tag title with standardized "and"/"&"
            const formattedTitle = tag
              .split(' ')
              .map((word, idx, arr) => {
                // Lowercase "and" or replace with "&"
                if (word.toLowerCase() === "and") {
                  return "&";
                }
                // Capitalize first letter of other words
                return word.charAt(0).toUpperCase() + word.slice(1);
              })
              .join(' ');
              
            // Get category style for this tag
            const categoryStyle = getCategoryStyle(lowercaseTag);
            const iconName = categoryStyle.icon || "BsStars";
            const titleColor = categoryStyle.color || "#302b63";
            
            return (
              <CardCarousel
                key={`home-tag-${index}`}
                data={taggedMovies}
                title={formattedTitle}
                showDuration={true}
                iconName={iconName}
                titleColor={titleColor}
              />
            );
          });
        })()}
      </Box>
    </Box>
  );
}

export default Home;
